import { EProductsInsurance, EProvince } from '@if/core/types'
import { Box, Stack, Typography } from '@mui/material'
import { SelectComponent } from '@src/components'
import { SelectValueListItem } from '@src/data/types'
import { ProductsInsurance, ProductsWorksheetDto } from '@src/data/types/ProductsWorksheetSchema'
import { ChangeEvent, memo, useEffect, useMemo, useState } from 'react'
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ProductsInsuranceDetails from './ProductsInsuranceDetails'

interface Props {
  insurancesProviders: SelectValueListItem[]
  warrantyProviders: SelectValueListItem[]
  register: UseFormRegister<ProductsWorksheetDto>
  watch: UseFormWatch<ProductsWorksheetDto>
  setValue: UseFormSetValue<ProductsWorksheetDto>
  errors: FieldErrors<ProductsWorksheetDto>
  getValues: UseFormGetValues<ProductsWorksheetDto>
  trigger: UseFormTrigger<ProductsWorksheetDto>
  stateIso: EProvince
  control: Control<ProductsWorksheetDto>
}

const ProductsInsurances = ({
  insurancesProviders,
  warrantyProviders,
  register,
  watch,
  setValue,
  errors,
  getValues,
  stateIso,
  control,
}: Props) => {
  const { t } = useTranslation()
  const today = useMemo(() => new Date(), [])
  const replacementInsuranceProvider = watch('replacementOrGapInsurance.provider') ?? ''
  const creditInsuranceProvider = watch('creditInsurance.provider') ?? ''
  const extendedWarrantyProvider = watch('extendedWarranty.provider') ?? ''

  const [replacementInsuranceProviderError, setReplacementInsuranceProviderError] = useState<string | undefined>(
    undefined,
  )
  const [creditInsuranceProviderError, setCreditInsuranceProviderError] = useState<string | undefined>(undefined)
  const [extendedWarrantyError, setExtendedWarrantyError] = useState<string | undefined>(undefined)
  const [worksheet, setWorksheet] = useState<ProductsWorksheetDto>(getValues())

  const [creditInsurance, replacementOrGapInsurance, extendedWarranty] = watch([
    'creditInsurance',
    'replacementOrGapInsurance',
    'extendedWarranty',
  ])

  useEffect(() => {
    const anyReplacementOrGapField = anyFieldProvided(replacementOrGapInsurance)
    const anyCreditField = anyFieldProvided(creditInsurance)
    const anyExtendedField = anyFieldProvided(extendedWarranty)

    setReplacementInsuranceProviderError(
      anyReplacementOrGapField && !replacementInsuranceProvider ? t('mixed.required', { ns: 'validation' }) : undefined,
    )
    setCreditInsuranceProviderError(
      anyCreditField && !creditInsuranceProvider ? t('mixed.required', { ns: 'validation' }) : undefined,
    )
    setExtendedWarrantyError(
      anyExtendedField && !extendedWarrantyProvider ? t('mixed.required', { ns: 'validation' }) : undefined,
    )

    const getPremium = (value: number | null | undefined) => {
      const amount = Number(value)
      return Number.isNaN(amount) ? 0 : amount
    }

    const amount =
      getPremium(replacementOrGapInsurance?.amount) +
      getPremium(creditInsurance?.amount) +
      getPremium(extendedWarranty?.amount)

    setValue('amountRequested', amount)
  }, [
    replacementInsuranceProvider,
    creditInsuranceProvider,
    extendedWarrantyProvider,
    setValue,
    getValues,
    creditInsurance?.amount,
    replacementOrGapInsurance?.amount,
    extendedWarranty?.amount,
    replacementOrGapInsurance?.police,
    creditInsurance?.police,
    extendedWarranty?.police,
    replacementOrGapInsurance?.provider,
    creditInsurance?.provider,
    extendedWarranty?.provider,
    replacementOrGapInsurance?.term,
    creditInsurance?.term,
    extendedWarranty?.term,
    replacementOrGapInsurance,
    creditInsurance,
    extendedWarranty,
    t,
  ])

  const anyFieldProvided = (values: ProductsInsurance | null) => {
    if (!values) return false
    return !!values.amount || !!values.term || !!values.police || !!values.endDate
  }

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      setWorksheet(values as ProductsWorksheetDto)
    })
    return () => unsubscribe()
  }, [watch])

  return (
    <Stack direction={{ lg: 'row', md: 'column' }} spacing={2} justifyContent="flex-start" sx={{ width: '100%' }}>
      <Stack direction="column" sx={{ height: '100%', flex: 2, minWidth: 0 }}>
        <Typography variant="h5">{t('worksheet.protectionPlan')}</Typography>
        <Stack direction="column" justifyContent="space-around" sx={{ flexGrow: 1, marginTop: 2 }}>
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SelectComponent
                error={replacementInsuranceProviderError}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setValue('replacementOrGapInsurance.provider', e.target.value)
                }}
                items={insurancesProviders}
                label={t('worksheet.provider')}
                value={worksheet.replacementOrGapInsurance?.provider}
              />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                {stateIso === EProvince.Quebec ? t('worksheet.replacementInsurance') : t('worksheet.gapInsurance')}
              </Typography>
              <ProductsInsuranceDetails
                register={register}
                insurance={EProductsInsurance.ReplacementOrGapInsurance}
                errors={errors}
                control={control}
                setValue={setValue}
                today={today}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SelectComponent
                error={creditInsuranceProviderError}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setValue('creditInsurance.provider', e.target.value)
                }}
                items={insurancesProviders}
                label={t('worksheet.provider')}
                value={worksheet.creditInsurance?.provider}
              />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                {t('worksheet.creditInsurance')}
              </Typography>
              <ProductsInsuranceDetails
                register={register}
                insurance={EProductsInsurance.CreditInsurance}
                errors={errors}
                control={control}
                setValue={setValue}
                today={today}
              />
            </Box>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="column" sx={{ height: '100%', flex: 1, minWidth: 0 }}>
        <Typography variant="h5">{t('worksheet.extendedWarranty')}</Typography>
        <Stack direction="column" justifyContent="space-around" sx={{ flexGrow: 1, marginTop: 2 }}>
          <SelectComponent
            items={warrantyProviders}
            error={extendedWarrantyError}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setValue('extendedWarranty.provider', e.target.value)
            }}
            label={t('worksheet.provider')}
            value={worksheet.extendedWarranty?.provider}
          />
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
            {t('worksheet.extendedWarranty')}
          </Typography>{' '}
          <ProductsInsuranceDetails
            register={register}
            insurance={EProductsInsurance.ExtendedWarranty}
            errors={errors}
            control={control}
            setValue={setValue}
            today={today}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

export default memo(ProductsInsurances)